import { useNotify, useRecordContext, useLoading } from "react-admin";
import { Grid, Typography } from "@mui/material";
import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import dataProvider from "../../../server/dataProvider";
import CustomDateTimeInput from "server/components/Fields/CustomDateTimeInput";
import Declarations from "../components/Declarations";
import RegularCampaignExamList from "./components/ExamList";
import Ibans from "../components/Ibans";
import MainRoomsList from "./components/MainRoomsList";
import SpecialitiesList from "../components/SpecialitiesList";
import RankingsInput from "../components/RankingsInput";
import { CampaignSubTypes } from "utils/constants";
import campaignHelper from "../helpers";
import NamesInput from "../components/Inputs/NamesInput";
import CitizenshipInput from "../components/Inputs/CitizenshipInput";
import CampaignTypeInput from "../components/Inputs/CampaignTypeInput";
import DegreeInput from "../components/Inputs/DegreeInput";
import DirectionInput from "../components/Inputs/DirectionInput";
import ChildCampaignInput from "../components/Inputs/ChildCampaignInput";
import Taxes from "../components/Taxes";
import AdmissionMarkerInput from "../components/Inputs/AdmissionMarkerInput";
import { administrationRequests } from "api/administrationApi";
import { useNotifyError } from "utils/useNotifyError";

const SaveForm = ({ isEdit, isCreate, isClone = false }) => {
  const record = useRecordContext();
  const loading = useLoading();
  const notify = useNotify();
  const notifyError = useNotifyError();

  const { setValue } = useFormContext();
  const campaignIsStarted = record?.campaignIsStarted;

  const validateStartDate = async (value, allValues) => {
    if (!value) {
      return "Полето е задължително";
    }
    if (value >= allValues.registrationTo) {
      return "Началната дата трябва да е по-малка от крайната";
    }
    if (isEdit) return;
    return await campaignHelper.checkAvailability({
      campaignId: record?.id,
      typeId,
      directionId,
      startDate: allValues.registrationFrom,
      endDate: allValues.registrationTo,
      degreeId,
      citizenshipIds,
      notify,
    });
  };

  const validateEndDate = async (value, allValues) => {
    if (!value) {
      return "Полето е задължително";
    }
    if (value <= allValues.registrationFrom) {
      return "Крайната дата трябва да е по-голяма от началната";
    }
    if (isEdit) return;
    return await campaignHelper.checkAvailability({
      campaignId: record?.id,
      typeId,
      directionId,
      startDate: allValues.registrationFrom,
      endDate: allValues.registrationTo,
      degreeId,
      citizenshipIds,
      notify,
    });
  };

  const handleStartDateChange = value => {
    setMinEndDate(value?._i || null);
  };

  const [minEndDate, setMinEndDate] = useState(record?.registrationFrom);
  const [excludedSpecialityIds, setExcludedSpecialityIds] = useState(
    record?.specialityDtos?.map(s => s.specialityId) | [],
  );
  const [directionId, setDirectionId] = useState(record?.directionId || null);
  const [degreeId, setDegreeId] = useState(record?.degreeId);
  const [typeId, setTypeId] = useState(record?.typeId);
  const [exams, setExams] = useState(record?.exams || []);
  const [citizenshipIds, setCitizenshipIds] = useState(() => record?.citizenship || []);
  const [specialityShowGender, setSpecialityShowGender] = useState([]);

  const citizenshipChange = event => {
    setCitizenshipIds(event.target.value);
  };

  const handleTypeChange = event => {
    setTypeId(event?.target?.value || event);
  };

  const handleSpecialityChange = async (_event, formDataSpecialities) => {
    const selectedSpecialitiesIds = formDataSpecialities
      ? formDataSpecialities.map(item => item.specialityId).filter(Boolean)
      : [];
    fetchExamSpecialities(selectedSpecialitiesIds);
    fetchSpecialitiesDistribution(selectedSpecialitiesIds);
  };

  const fetchSpecialitiesDistribution = async specialityIds => {
    const data = await campaignHelper.getSpecialitiesDistribution(specialityIds);
    setSpecialityShowGender(data);
  };

  const fetchExamSpecialities = async selectedSpecialityIds => {
    setExcludedSpecialityIds(selectedSpecialityIds);
    if (!selectedSpecialityIds?.length) {
      setExams([]);
      setValue("exams", []);
      return;
    }
    try {
      const { data } = await dataProvider.customRequest(
        administrationRequests.getExamSpecialitiesSelect(selectedSpecialityIds),
        null,
        "GET",
      );
      const newExams = data.map(item => ({
        examId: item.id,
        examName: item.name,
        examTypeId: null,
        examCategoryId: null,
      }));
      setExams(newExams);

      setValue("exams", newExams);
    } catch (err) {
      notifyError(err);
    }
  };

  const handleDegreeChange = event => {
    setDegreeId(event.target.value);
  };

  const handleDirectionChange = newDirectionId => {
    setDirectionId(newDirectionId);
    setValue("specialityDtos", [{}]);
  };

  const handleRemoveSpeciality = removedId => {
    const updatedSpecialityIdsIds = excludedSpecialityIds.filter(id => id !== removedId);
    fetchExamSpecialities(updatedSpecialityIdsIds);
  };

  const handleRemoveExam = newExams => {
    setExams(newExams);
  };

  return (
    <>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
        >
          {isEdit &&
            (isEdit && campaignIsStarted ? (
              <Typography
                color={theme => theme.palette.success.main}
                sx={{ textAlign: "center", fontWeight: "bold" }}
              >
                Кампанията е стартирала
              </Typography>
            ) : (
              <Typography
                color={theme => theme.palette.success.main}
                sx={{ textAlign: "center", fontWeight: "bold" }}
              >
                Кампанията предстои да стартира
              </Typography>
            ))}
        </Grid>
        <Grid
          item
          xs={12}
        >
          <AdmissionMarkerInput readOnly={!isClone && (campaignIsStarted || loading)} />
        </Grid>

        <Grid
          item
          xs={8}
        >
          <NamesInput readOnly={!isClone && loading && isEdit} />
        </Grid>
        <Grid
          item
          xs={4}
        >
          <CitizenshipInput
            selectedIds={citizenshipIds}
            isArray
            isBgAndForeignersSeperated
            handleChange={citizenshipChange}
            readOnly={!isClone && (campaignIsStarted || (loading && isEdit))}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={2.3}
        >
          <CustomDateTimeInput
            label="Начало на регистрация"
            source="registrationFrom"
            required={true}
            validate={validateStartDate}
            onChange={event => {
              handleStartDateChange(event);
            }}
            readOnly={!isClone && (campaignIsStarted || (loading && isEdit))}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={2.3}
        >
          <CustomDateTimeInput
            label="Край на регистрация"
            source="registrationTo"
            required={true}
            validate={validateEndDate}
            inputProps={{
              min: minEndDate,
            }}
            helperText={false}
            readOnly={loading && isEdit}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <CampaignTypeInput
            subTypeId={CampaignSubTypes.Regular}
            readOnly={!isClone && (campaignIsStarted || (loading && isEdit))}
            handleChange={handleTypeChange}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <DegreeInput
            handleChange={handleDegreeChange}
            readOnly={!isClone && (campaignIsStarted || (loading && isEdit))}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <DirectionInput
            required
            handleChange={handleDirectionChange}
            filterByUser
            filterOnlyForCampaigns
            readOnly={!isClone && (campaignIsStarted || (loading && isEdit))}
          />
        </Grid>
        {directionId && (
          <>
            <SpecialitiesList
              directionId={directionId}
              hasAvaliablePaidEducationCapacity={true}
              excludedSpecialityIds={excludedSpecialityIds}
              campaignIsStarted={campaignIsStarted}
              handleSpecialityChange={handleSpecialityChange}
              handleRemoveSpeciality={handleRemoveSpeciality}
              specialityShowGender={specialityShowGender}
              loading={loading && isEdit}
              isEdit={isEdit}
              isClone={isClone}
            />
            {exams && (
              <>
                <MainRoomsList
                  record={record}
                  isDisabled={!isClone && (campaignIsStarted || (loading && isEdit))}
                />

                <Grid
                  item
                  xs={12}
                  md={12}
                >
                  <RegularCampaignExamList
                    campaignIsStarted={campaignIsStarted}
                    examsChanged={handleRemoveExam}
                    readOnly={!isClone && (campaignIsStarted || (loading && isEdit))}
                  />
                </Grid>
              </>
            )}
          </>
        )}
        <Taxes
          campaignSubTypeId={CampaignSubTypes.Regular}
          citizenshipIds={citizenshipIds}
          examsCount={exams?.length || 0}
          specialityIds={excludedSpecialityIds || []}
          isCreate={isClone ? false : isCreate}
        />
        <Declarations
          citizenshipIds={citizenshipIds}
          isDisabled={!isClone && (campaignIsStarted || (loading && isEdit))}
        />
        <RankingsInput isDisabled={!isClone && loading} />
        <Ibans isDisabled={loading && isEdit} />
        <Grid
          item
          md={5}
          xs={12}
        >
          <ChildCampaignInput
            childCampaignSubTypeId={CampaignSubTypes.Preliminary}
            readOnly={!isClone && (campaignIsStarted || (loading && isEdit))}
          />
        </Grid>
      </Grid>
    </>
  );
};
export default SaveForm;
